import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { AlertTriangle, FileText, MapPin, Phone } from 'lucide-react';
import { SEO } from '../components/SEO';

export const LegalPage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO 
        title="Réglementation Apiculture Urbaine | Lois & Obligations - UrbanHive"
        description="Tout savoir sur la réglementation de l'apiculture urbaine en France : déclaration obligatoire DDPP, distances réglementaires, assurance et contacts utiles. Installez votre ruche en toute légalité."
        keywords="réglementation apiculture urbaine, loi ruche balcon, déclaration ruche DDPP, distance réglementaire ruche, assurance apiculture, législation abeilles ville"
        url="/legal"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section className="relative py-24 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85" 
              alt="Réglementation apiculture"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
                Réglementation de l'Apiculture Urbaine
              </h1>
              <p className="text-lg text-white/90">
                Tout ce qu'il faut savoir sur les lois et obligations pour installer des ruches en ville.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-12">
              
              {/* Déclaration obligatoire */}
              <Card className="border-2 border-red-200 bg-red-50">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Déclaration Obligatoire</h2>
                      <p className="text-gray-700 mb-4">
                        Toute personne détenant au moins une ruche doit obligatoirement la déclarer à la Direction Départementale de la Protection des Populations (DDPP).
                      </p>
                      <div className="bg-white p-4 rounded-lg">
                        <p className="font-semibold text-gray-900 mb-2">Comment déclarer ?</p>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li>• En ligne sur <a href="https://mesdemarches.agriculture.gouv.fr" className="text-honey hover:underline" target="_blank" rel="noopener noreferrer">mesdemarches.agriculture.gouv.fr</a></li>
                          <li>• Par courrier au formulaire Cerfa n°13995*04</li>
                          <li>• Déclaration à renouveler chaque année entre le 1er septembre et le 31 décembre</li>
                          <li>• Vous recevrez un numéro NAPI (Numéro d'Apiculteur) à afficher sur vos ruches</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Distances légales */}
              <Card className="border-2 border-sage/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-sage" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Distances Réglementaires</h2>
                      <p className="text-gray-600 mb-4">Les distances varient selon les départements, mais les règles générales sont :</p>
                      
                      <div className="space-y-4">
                        <div className="p-4 bg-cream rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl font-bold text-sage">5m</span>
                            <span className="text-gray-700">minimum des propriétés voisines</span>
                          </div>
                          <p className="text-sm text-gray-600">Ou si une haie, palissade, mur de 2m de haut sépare les ruches des propriétés voisines</p>
                        </div>
                        
                        <div className="p-4 bg-cream rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl font-bold text-honey">100m</span>
                            <span className="text-gray-700">des établissements publics</span>
                          </div>
                          <p className="text-sm text-gray-600">Écoles, hôpitaux, casernes, campings (sauf autorisation spéciale)</p>
                        </div>
                        
                        <div className="p-4 bg-cream rounded-lg">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl font-bold text-sage">20m</span>
                            <span className="text-gray-700">des voies publiques</span>
                          </div>
                          <p className="text-sm text-gray-600">Réduit à 10m si haie protectrice</p>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <strong>Important :</strong> Ces distances peuvent varier selon les arrêtés préfectoraux. Consultez votre mairie ou préfecture pour connaître les règles spécifiques à votre commune.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assurance */}
              <Card className="border-2 border-honey/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-honey/20 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-6 w-6 text-honey" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Assurance Responsabilité Civile</h2>
                      <p className="text-gray-700 mb-4">
                        Bien que non obligatoire, une assurance responsabilité civile spécifique "apiculteur" est fortement recommandée.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-start">
                          <span className="text-honey mr-2">✓</span>
                          <span className="text-gray-700">Couvre les dommages causés à des tiers par vos abeilles</span>
                        </div>
                        <div className="flex items-start">
                          <span className="text-honey mr-2">✓</span>
                          <span className="text-gray-700">Coût moyen : 40-80€ par an pour 5-10 ruches</span>
                        </div>
                        <div className="flex items-start">
                          <span className="text-honey mr-2">✓</span>
                          <span className="text-gray-700">Incluse dans l'adhésion aux syndicats apicoles (30-50€/an)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact */}
              <Card className="border-2 border-sage/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-sage" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-6">Contacts Utiles</h2>
                      <div className="space-y-6">
                        <div className="p-4 bg-cream rounded-lg">
                          <h3 className="font-semibold text-gray-900 mb-2">DDPP (Direction Départementale de la Protection des Populations)</h3>
                          <p className="text-sm text-gray-600 mb-3">Déclaration des ruches et réglementation sanitaire</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-sage" />
                              <span className="text-gray-700">Consultez votre département sur <a href="https://www.service-public.fr" className="text-honey hover:underline" target="_blank" rel="noopener noreferrer">service-public.fr</a></span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                              <a href="https://mesdemarches.agriculture.gouv.fr" className="text-honey hover:underline" target="_blank" rel="noopener noreferrer">
                                mesdemarches.agriculture.gouv.fr
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4 bg-cream rounded-lg">
                          <h3 className="font-semibold text-gray-900 mb-2">Syndicats apicoles départementaux</h3>
                          <p className="text-sm text-gray-600 mb-3">Formation, conseil et assurance</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                              <a href="https://www.unaf-apiculture.info" className="text-honey hover:underline" target="_blank" rel="noopener noreferrer">
                                unaf-apiculture.info
                              </a>
                              <span className="text-gray-500">(Union Nationale de l'Apiculture Française)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-sage" />
                              <span className="text-gray-700">01 45 43 13 60</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                              <a href="mailto:contact@unaf-apiculture.info" className="text-honey hover:underline">
                                contact@unaf-apiculture.info
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4 bg-cream rounded-lg">
                          <h3 className="font-semibold text-gray-900 mb-2">Mairie de votre commune</h3>
                          <p className="text-sm text-gray-600 mb-3">Arrêtés locaux spécifiques à votre ville</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-sage" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                              <a href="https://www.service-public.fr/particuliers/vosdroits/F14868" className="text-honey hover:underline" target="_blank" rel="noopener noreferrer">
                                Trouver ma mairie
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-sage" />
                              <span className="text-gray-700">Consultez l'annuaire de votre département</span>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-honey/10 rounded-lg border-2 border-honey/20">
                          <h3 className="font-semibold text-gray-900 mb-2">Urgence sanitaire apicole</h3>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-honey" />
                              <span className="text-gray-700 font-medium">0 800 97 49 10</span>
                              <span className="text-gray-500">(numéro vert gratuit)</span>
                            </div>
                            <p className="text-gray-600 text-xs">En cas de suspicion de maladie contagieuse dans votre rucher</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};